import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useState } from 'react'
import { Edit, FileDownload, OverflowMenu, TrashDelete } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { MatterSubmenu } from '@/chat-common/conversation_menu/MatterSubMenu'
import { MobileMatterSubMenuContent } from '@/chat-common/conversation_menu/MobileMatterSubMenuContent'
import { DEVICE_TYPE, useDeviceType } from '@/hooks/useDeviceType'

export interface RecentActivityMenuProps {
  onDelete?: () => void
  onRename?: () => void
  onDownload?: () => void
  onMoveToMatter?: (matterId: string, matterName?: string) => void
  matterId?: string | null
}

/**
 * Overflow menu for a recent activity item: Delete, Rename, Download, Move to matter.
 * Supports mobile (full-screen matter list) and desktop (nested submenu).
 */
export function RecentActivityMenu({ onDelete, onRename, onDownload, onMoveToMatter, matterId = null }: RecentActivityMenuProps) {
  const deviceType = useDeviceType()
  const isMobile = deviceType === DEVICE_TYPE.MOBILE
  const [mattersContentOpen, setMattersContentOpen] = useState(false)

  return (
    <div
      className="absolute right-0 top-1/2 -translate-y-1/2 opacity-100 transition-opacity md:opacity-0 md:hover-capable:group-hover/card:opacity-100 md:group-focus-within/card:opacity-100"
      // Keep menu interaction (pointer and keyboard) from bubbling to the card's click/Enter handler
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <DropdownMenu.Root
        onOpenChange={(open) => {
          if (!open) setMattersContentOpen(false)
        }}
      >
        <DropdownMenu.Trigger asChild>
          <IconButton
            aria-label="Activity actions"
            className="focus-visible:outline-offset-0"
            color="black"
            icon={<OverflowMenu />}
            size="md"
            type="button"
            variant="plain"
          />
        </DropdownMenu.Trigger>
        {mattersContentOpen && onMoveToMatter ? (
          <MobileMatterSubMenuContent setMattersContentOpen={setMattersContentOpen} fromMatterId={matterId} handleMatterSelect={onMoveToMatter} />
        ) : (
          <MenuContent side="bottom" align="end">
            <MenuItemGroup>
              {onDelete && <MenuItem label="Delete" icon={TrashDelete} onSelect={onDelete} variant="destructive" />}
              {onRename && <MenuItem label="Rename" icon={Edit} onSelect={onRename} variant="neutral" />}
              {onDownload && <MenuItem label="Download" icon={FileDownload} onSelect={onDownload} variant="neutral" />}
              {onMoveToMatter && (
                <MatterSubmenu
                  fromMatterId={matterId}
                  handleMatterSelect={onMoveToMatter}
                  isMobile={isMobile}
                  mattersContentOpen={mattersContentOpen}
                  setMattersContentOpen={setMattersContentOpen}
                />
              )}
            </MenuItemGroup>
          </MenuContent>
        )}
      </DropdownMenu.Root>
    </div>
  )
}
